import * as qs from 'qs'
import type { UseFetchOptions } from 'nuxt/app'
import { mergeKftEventDescriptionSettingsIncludes } from '~/utils/kftEventDescriptionIncludes'
import type { DateRequestSimilarEventsResponse } from './types/event'

export const useSubscribeEmailToEventUpdates = () => {
  /** Wrapper for API requests */
  const error = ref(null)
  const loading = ref(false)
  const subscribeEmailToEventUpdates = async ({
    permalink,
    email,
  }: {
    permalink: string
    email: string
  }) => {
    // Logger.debug(`useSubscribeEmailToEventUpdates/${id}/subscribeEmailToEventUpdates`, id)

    try {
      loading.value = true
      await $fetch(`v1/store/events/${permalink}/subscribe`, {
        method: 'POST',
        body: { email },
      })

      error.value = null
    } catch (err) {
      error.value = (err as any)?.response?.data || err
      // Logger.error(`useSubscribeEmailToEventUpdates/${id}/subscribeEmailToEventUpdates`, err)
    } finally {
      loading.value = false
    }
  }

  return {
    subscribeEmailToEventUpdates,
    error,
  }
}

export const useFetchEventByPermalink = <T, R = T>(
  permalink: string,
  options?: UseFetchOptions<T, R>
) =>
  useKftApiFetch<T, R>(
    `v1/store/events/${permalink}?${qs.stringify(
      {
        include: mergeKftEventDescriptionSettingsIncludes([
          'address.locality',
          'supplier',
          'reviews',
        ]),
      },
      { arrayFormat: 'comma' }
    )}`,
    options
  )

export const useFetchSupplierEvents = <T>(
  supplierId: string,
  orderByProperty?: string,
  options?: UseFetchOptions<T>
) => {
  const isSubdomain = useIsSubdomain()
  const paramsObj = {
    limit: '50',
    include: ['categories', 'address.locality', 'supplier'],
    ...(orderByProperty && { orderBy: orderByProperty }),
    search: `supplier_id:${supplierId};hasEvent:false;${isSubdomain.value ? 'embeddedPublished:1' : 'konfettiPublished:1'}`,
  }

  const url = `v1/store/events?${qs.stringify(paramsObj, {
    // encodeValuesOnly: true, // prettify URL
    encode: false,
    arrayFormat: 'comma',
  })}`
  return useKftApiFetch(url, options)
}

export const useFetchSimilarEvents = <T>(
  eventPermalink: string,
  partnerId: string,
  options?: UseFetchOptions<T>
) => {
  const paramsObj = {
    orderBy: 'distance',
    sortedBy: 'asc',
    search: `supplier_id:${partnerId};hasEvent:true`,
    include: ['supplier', 'categories'],
    searchFields: 'supplier_id:!=',
  }

  const url = `v1/store/events/${eventPermalink}/similar?${qs.stringify(
    paramsObj,
    {
      encode: false,
      arrayFormat: 'comma',
    }
  )}`
  return useKftApiFetch(url, options)
}

export const useFetchDateRequestSimilarEvents = <
  T = DateRequestSimilarEventsResponse,
>(
  dateRequestId: string,
  options?: UseFetchOptions<T>
) => {
  const url = `v1/store/date-requests/${dateRequestId}/similar-events?${qs.stringify(
    {
      include: ['supplier', 'categories'],
    },
    {
      encode: false,
      arrayFormat: 'comma',
    }
  )}`
  return useKftApiFetch(url, options)
}

export const useFetchSimilarOnlineEvents = <T>(
  category: string,
  options?: UseFetchOptions<T>
) => {
  const paramsObj = {
    limit: '8',
    include: ['supplier', 'categories'],
    // search: { hasEvent: true, type: 'ONLINE', category },
    search: `hasEvent:true;type:ONLINE;category:${category}`,
  }

  const url = `v1/store/events?${qs.stringify(paramsObj, {
    encode: false,
    arrayFormat: 'comma',
  })}`
  return useKftApiFetch(url, options)
}

export interface SimilarKitsQueryOptions {
  category?: string
  limit?: number
  page?: number
  hasPromotion?: boolean
  /** Whole euros; passed as query hints if the API supports them */
  priceMinEur?: number
  priceMaxEur?: number
}

export const getSimilarKitsQueryParams = (
  opts: SimilarKitsQueryOptions
): Record<string, unknown> => {
  const searchParts = ['specialization:KIT', 'hasEvent:false']
  if (opts.category) searchParts.push(`category:${opts.category}`)
  if (opts.hasPromotion) searchParts.push('has_promotions:true')

  const params: Record<string, unknown> = {
    limit: opts.limit ?? 8,
    page: opts.page ?? 1,
    include: ['supplier', 'categories'],
    search: searchParts.join(';'),
    orderBy: ['has_events', 'has_promotion', 'trending_90_days'],
    sortedBy: 'desc',
  }

  if (opts.priceMinEur != null) {
    params.min_default_price_amount = Math.round(opts.priceMinEur * 100)
  }
  if (opts.priceMaxEur != null) {
    params.max_default_price_amount = Math.round(opts.priceMaxEur * 100)
  }

  return params
}

export const useFetchSimilarKits = <T>(
  category: string,
  limit?: number,
  options?: UseFetchOptions<T>,
  extra?: Pick<
    SimilarKitsQueryOptions,
    'page' | 'hasPromotion' | 'priceMinEur' | 'priceMaxEur'
  >
) => {
  const paramsObj = getSimilarKitsQueryParams({
    category,
    limit,
    page: extra?.page,
    hasPromotion: extra?.hasPromotion,
    priceMinEur: extra?.priceMinEur,
    priceMaxEur: extra?.priceMaxEur,
  })

  const url = `v1/store/events?${qs.stringify(paramsObj, {
    encode: false,
    arrayFormat: 'comma',
  })}`
  return useKftApiFetch(url, options)
}
